import type {
  ComponentNode,
  ContainerNode,
  BehaviorGraph,
  LayoutListComponentProps,
  LayoutListMode,
  LayoutNode,
} from "../layout/types.js";
import { getDataBindingSource } from "../layout/types.js";
import { buildLayoutListScopedRowData } from "../layout/layout-row-binding-fingerprint.js";
import {
  deriveLayoutListRowKey,
  resolveLayoutListRowOffsets,
} from "../layout/layout-list-rows.js";
import { getLayoutListRowsFromBinding } from "../layout/resolution.js";
import { applyPatchToLayoutListNode } from "./applyRuntimePatches.js";
import { collectBehaviorDependencies } from "./compileEvents.js";
import { compileBehavior } from "./compileBehavior.js";
import { compileContainerNode } from "./compileContainerNode.js";
import {
  coerceToBoolean,
  compileNodeStyle,
  transitionsTouchTopLeft,
} from "./helpers.js";
import { transformLayoutListRows } from "./list-data-pipeline.js";
import {
  applyRuntimeDomTargetFlags,
  runCompiledNodeBehavior,
  withCurrentNodeGeometry,
} from "./applyNodeBehavior.js";
import type {
  CompiledContainerNode,
  CompiledLayoutListNode,
  CompileNodeContext,
  RenderContainerNode,
  RenderLayoutListNode,
  RuntimeEffect,
  RuntimeContext,
  RuntimeFlowContinuation,
  RuntimeTimedFlowStep,
  CompiledEventHandler,
  RuntimeListTransitionDispatch,
  ListFilterTransitionHandler,
} from "./types.js";
import {
  buildDependencySignature,
  evaluateWithRenderCache,
} from "./renderCache.js";
import { uniqueDependencies } from "./method.js";
import { resolvePath } from "../layout/resolution.js";
import { offsetRenderNodePosition } from "./renderClip.js";

const DEFAULT_LAYOUT_LIST_MAX_ITEMS = 10;
const ROW_SCOPED_ROOTS = new Set([
  "row",
  "item",
  "line",
  "layoutListRowItem",
  "rowIndex",
  "iterationItem",
  "iterationIndex",
  "iterationItemA",
  "iterationItemB",
]);

function getPathRoot(path: string): string {
  return path.split(".")[0] ?? path;
}

function isRowScopedDependency(path: string): boolean {
  return ROW_SCOPED_ROOTS.has(getPathRoot(path));
}

function stableSerialize(value: unknown): string {
  if (value == null) return "null";
  if (typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableSerialize(entry)).join(",")}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>).sort(
    ([left], [right]) => left.localeCompare(right),
  );
  return `{${entries
    .map(
      ([key, nestedValue]) =>
        `${JSON.stringify(key)}:${stableSerialize(nestedValue)}`,
    )
    .join(",")}}`;
}

function getAnimationEffectWaitMs(effect: RuntimeEffect): number {
  if (effect.type !== "animation.play") return 0;
  const payloadDelay =
    typeof effect.delayMs === "number"
      ? effect.delayMs
      : (effect.payload.delayMs ?? 0);
  const payloadDuration =
    typeof effect.durationMs === "number"
      ? effect.durationMs
      : (effect.payload.durationMs ?? 0);
  return Math.max(0, payloadDelay) + Math.max(0, payloadDuration);
}

function remapAnimationEffectTargetKey(
  effect: RuntimeEffect,
  fromKey: string,
  toKey: string,
) {
  if (
    (effect.type === "animation.play" || effect.type === "animation.cancel") &&
    effect.targetNodeKey === fromKey
  ) {
    effect.targetNodeKey = toKey;
  }
}

function getTimedStepDurationMs(step: RuntimeTimedFlowStep): number {
  return step.effects.reduce(
    (maxDuration, effect) =>
      Math.max(maxDuration, step.atMs + getAnimationEffectWaitMs(effect)),
    step.atMs,
  );
}

function mergeTimedFlowStep(
  stepsByAtMs: Map<number, RuntimeTimedFlowStep>,
  atMs: number,
  result: {
    patches: RuntimeTimedFlowStep["patches"];
    effects: RuntimeTimedFlowStep["effects"];
  },
) {
  if (result.patches.length === 0 && result.effects.length === 0) {
    return;
  }

  const step = stepsByAtMs.get(atMs) ?? {
    atMs,
    patches: [],
    effects: [],
  };
  step.patches.push(...result.patches);
  step.effects.push(...result.effects);
  stepsByAtMs.set(atMs, step);
}

type TimedFlowContinuation = {
  handler: CompiledEventHandler;
  continuation: RuntimeFlowContinuation;
};

type TimedFlowHandler = {
  handler: CompiledEventHandler;
  ctx: RuntimeContext;
};

function sortContinuations(
  continuations: TimedFlowContinuation[],
): TimedFlowContinuation[] {
  return [...continuations].sort(
    (left, right) => left.continuation.wakeAt - right.continuation.wakeAt,
  );
}

function collectTimedFlowSteps(
  handlers: TimedFlowHandler[],
  baseNow: number,
): RuntimeTimedFlowStep[] {
  const stepsByAtMs = new Map<number, RuntimeTimedFlowStep>();
  let pendingContinuations: TimedFlowContinuation[] = [];
  let resumeIterationCount = 0;

  for (const { handler, ctx } of handlers) {
    const result = handler.flow.run({ ...ctx, __nowMs: baseNow });
    mergeTimedFlowStep(stepsByAtMs, 0, result);
    pendingContinuations.push(
      ...result.continuations.map((continuation) => ({
        handler,
        continuation,
      })),
    );
  }

  while (pendingContinuations.length > 0 && resumeIterationCount < 100) {
    resumeIterationCount += 1;
    const [pending, ...rest] = sortContinuations(pendingContinuations);
    pendingContinuations = rest;

    if (!pending) {
      break;
    }

    const { handler, continuation } = pending;
    const atMs = Math.max(0, continuation.wakeAt - baseNow);
    const result = handler.flow.run(
      {
        ...continuation.ctx,
        __nowMs: continuation.wakeAt,
        __resumeContinuations: [continuation],
      },
      { resumeOnly: true },
    );
    mergeTimedFlowStep(stepsByAtMs, atMs, result);
    pendingContinuations.push(
      ...result.continuations.map((nextContinuation) => ({
        handler,
        continuation: nextContinuation,
      })),
    );
  }

  return [...stepsByAtMs.values()].sort(
    (left, right) => left.atMs - right.atMs,
  );
}

type CompiledRowTemplate = {
  name: string;
  template: ContainerNode;
  compiled: CompiledContainerNode;
};

type RowCacheEntry = {
  signature: string;
  render: RenderContainerNode;
  outerWidth: number;
  outerHeight: number;
  template: CompiledRowTemplate;
};

type SourceRowEntry = {
  rowKey: string;
  item: unknown;
  originalIndex: number;
};

type DisplayRowEntry = SourceRowEntry & {
  transitionDispatch?: RuntimeListTransitionDispatch;
  isExiting?: boolean;
  exitUntil?: number;
};

type RuntimeCurrentNodeGeometry = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
};

function behaviorGraphHasAnimatedEvent(
  graph: BehaviorGraph | undefined,
  eventKind: string,
): boolean {
  if (!graph) return false;

  const nodesById = new Map(graph.nodes.map((entry) => [entry.id, entry]));
  const outgoingByNodeId = new Map<string, typeof graph.connections>();
  for (const connection of graph.connections) {
    const outgoing = outgoingByNodeId.get(connection.fromNodeId) ?? [];
    outgoing.push(connection);
    outgoingByNodeId.set(connection.fromNodeId, outgoing);
  }

  const queue = graph.nodes
    .filter(
      (entry) =>
        entry.type === "event" && "kind" in entry && entry.kind === eventKind,
    )
    .map((entry) => entry.id);
  const visited = new Set<string>();

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);

    const node = nodesById.get(nodeId);
    if (node?.type === "action" && node.kind === "animPlay") {
      return true;
    }

    for (const connection of outgoingByNodeId.get(nodeId) ?? []) {
      queue.push(connection.toNodeId);
    }
  }

  return false;
}

function layoutSubtreeHasAnimatedEvent(
  node: LayoutNode,
  eventKind: string,
): boolean {
  if (behaviorGraphHasAnimatedEvent(node.behavior, eventKind)) {
    return true;
  }

  if (node.kind === "container") {
    return node.children.some((child) =>
      layoutSubtreeHasAnimatedEvent(child, eventKind),
    );
  }

  if (node.kind === "component" && node.component === "layoutList") {
    const props = (node.props ?? {}) as LayoutListComponentProps;
    if (
      props.rowTemplate &&
      layoutSubtreeHasAnimatedEvent(props.rowTemplate, eventKind)
    ) {
      return true;
    }

    return (props.rowVariants ?? []).some(
      (variant) =>
        variant.template &&
        layoutSubtreeHasAnimatedEvent(variant.template, eventKind),
    );
  }

  return false;
}

function getLayoutNodeGeometry(node: LayoutNode): RuntimeCurrentNodeGeometry {
  return {
    x: node.x,
    y: node.y,
    width: node.width,
    height: node.height,
    rotation: node.rotation ?? 0,
  };
}

type ListLifecycleState = {
  initialized: boolean;
  phase: "idle" | "exiting";
  rows: DisplayRowEntry[];
  pendingRows?: SourceRowEntry[];
  pendingEnterKeys?: Set<string>;
  pendingEnterKind?: RuntimeListTransitionDispatch["kind"];
  lastFilterKey?: string;
  nextDispatchId: number;
};

function getEffectiveListMode(
  props: LayoutListComponentProps,
  ctx: RuntimeContext,
): LayoutListMode {
  if (ctx.__isEditor === true) {
    return props.mode ?? "preview";
  }

  return "preview";
}

export function compileLayoutListNode(
  node: ComponentNode,
  compileContext: CompileNodeContext,
): CompiledLayoutListNode {
  if (node.component !== "layoutList") {
    throw new Error("Node is not a layoutList component");
  }

  const props = (node.props ?? {}) as LayoutListComponentProps;
  const rowTemplate = props.rowTemplate;

  if (!rowTemplate) {
    throw new Error("layoutList requires rowTemplate");
  }

  const defaultTemplate: CompiledRowTemplate = {
    name: "__default",
    template: rowTemplate,
    compiled: compileContainerNode(
      rowTemplate,
      compileContext,
    ) as CompiledContainerNode,
  };

  const variantTemplates = new Map<string, CompiledRowTemplate>();

  for (const variant of props.rowVariants ?? []) {
    const name = String(variant.name ?? "").trim();
    if (!name) {
      continue;
    }

    variantTemplates.set(name, {
      name,
      template: variant.template,
      compiled: compileContainerNode(
        variant.template,
        compileContext,
      ) as CompiledContainerNode,
    });
  }

  const compiledVariants = Array.from(variantTemplates.values());
  const baseStyle = compileNodeStyle(node.style, node.transitions);
  const behavior = node.behavior
    ? compileBehavior(
        node.behavior,
        node.id,
        compileContext.catalogDeps,
        undefined,
        compileContext.layoutNodeById,
      )
    : undefined;
  const maxItems = props.maxItems ?? DEFAULT_LAYOUT_LIST_MAX_ITEMS;
  const direction = props.direction ?? "column";
  const listSourceKey = getDataBindingSource(node.dataBinding);
  const rowVariantRules = behavior?.rowVariantRules ?? [];
  const computedListSource = behavior?.listSource;
  const listFilterRules = behavior?.listFilterRules ?? [];
  const listSortRules = behavior?.listSortRules ?? [];
  const listFilterTransitionBucket =
    compileContext.listFilterTransitionBucketsByListId?.get(node.id);
  const listFilterExitHandlers = listFilterTransitionBucket?.exitHandlers ?? [];
  const listFilterEnterHandlers =
    listFilterTransitionBucket?.enterHandlers ?? [];
  const externalListFilterExitHandlers = listFilterExitHandlers.filter(
    (entry) => !entry.rowScoped,
  );
  const externalListFilterEnterHandlers = listFilterEnterHandlers.filter(
    (entry) => !entry.rowScoped,
  );
  const hasListFilterTransitionHandlers =
    listFilterExitHandlers.length > 0 || listFilterEnterHandlers.length > 0;
  const hasListFilterExitAnimations = listFilterExitHandlers.some(
    (entry) => entry.handler.flow.requiresRuntimeDomTarget === true,
  );
  const hasRowExitAnimations = [
    rowTemplate,
    ...compiledVariants.map((entry) => entry.template),
  ].some((template) => layoutSubtreeHasAnimatedEvent(template, "onListExit"));
  const listFilterTransitionDependencies = uniqueDependencies(
    listFilterExitHandlers
      .concat(listFilterEnterHandlers)
      .flatMap((entry) => [
        ...entry.handler.flow.dependencies,
        ...(entry.handler.watchDependencies ?? []),
      ]),
  );
  const filterKeyDependencies = uniqueDependencies(
    listFilterRules.flatMap((rule) =>
      rule.dependencies.filter((dependency) => {
        if (!dependency || isRowScopedDependency(dependency)) {
          return false;
        }
        if (
          listSourceKey &&
          (dependency === listSourceKey ||
            dependency.startsWith(`${listSourceKey}.`))
        ) {
          return false;
        }
        return true;
      }),
    ),
  );

  const rowRenderCache = new Map<string, RowCacheEntry>();
  const lifecycleState: ListLifecycleState = {
    initialized: false,
    phase: "idle",
    rows: [],
    nextDispatchId: 0,
  };

  const createListTransitionDispatch = (
    kind: RuntimeListTransitionDispatch["kind"],
    rowKey: string,
  ): RuntimeListTransitionDispatch => ({
    id: ++lifecycleState.nextDispatchId,
    kind,
    listNodeId: node.id,
    rowKey,
  });

  const withEnterDispatch = (
    row: SourceRowEntry,
    kind: RuntimeListTransitionDispatch["kind"] = "onListEnter",
  ): DisplayRowEntry => ({
    ...row,
    transitionDispatch: createListTransitionDispatch(kind, row.rowKey),
  });

  const completeExitPhase = () => {
    const pendingRows = lifecycleState.pendingRows ?? [];
    const pendingEnterKeys = lifecycleState.pendingEnterKeys ?? new Set();
    const pendingEnterKind = lifecycleState.pendingEnterKind ?? "onListEnter";
    lifecycleState.rows = pendingRows.map((row) =>
      pendingEnterKeys.has(row.rowKey)
        ? withEnterDispatch(row, pendingEnterKind)
        : row,
    );
    lifecycleState.phase = "idle";
    lifecycleState.pendingRows = undefined;
    lifecycleState.pendingEnterKeys = undefined;
    lifecycleState.pendingEnterKind = undefined;
  };

  const syncLifecycleRows = (
    sourceRows: SourceRowEntry[],
    nowMs: number,
    effectiveMode: LayoutListMode,
    currentFilterKey: string | undefined,
    hasListFilterTransitionHandlers: boolean,
  ): DisplayRowEntry[] => {
    if (effectiveMode === "edit") {
      lifecycleState.initialized = true;
      lifecycleState.phase = "idle";
      lifecycleState.pendingRows = undefined;
      lifecycleState.pendingEnterKeys = undefined;
      lifecycleState.pendingEnterKind = undefined;
      lifecycleState.lastFilterKey = currentFilterKey;
      lifecycleState.rows = sourceRows;
      return lifecycleState.rows;
    }

    if (!lifecycleState.initialized) {
      lifecycleState.initialized = true;
      lifecycleState.lastFilterKey = currentFilterKey;
      lifecycleState.rows = sourceRows.map((row) => withEnterDispatch(row));
      return lifecycleState.rows;
    }

    if (lifecycleState.phase === "exiting") {
      const maxExitUntil = lifecycleState.rows.reduce(
        (max, row) =>
          Math.max(max, row.isExiting ? (row.exitUntil ?? nowMs) : nowMs),
        nowMs,
      );
      if (maxExitUntil > nowMs) {
        lifecycleState.pendingRows = sourceRows;
        return lifecycleState.rows;
      }
      completeExitPhase();
      return lifecycleState.rows;
    }

    const filterKeyChanged =
      currentFilterKey != null &&
      lifecycleState.lastFilterKey != null &&
      currentFilterKey !== lifecycleState.lastFilterKey;
    lifecycleState.lastFilterKey = currentFilterKey;

    if (filterKeyChanged && hasListFilterTransitionHandlers) {
      const activeRows = lifecycleState.rows.filter((row) => !row.isExiting);
      lifecycleState.phase = "exiting";
      lifecycleState.pendingRows = sourceRows;
      lifecycleState.pendingEnterKeys = new Set(
        sourceRows.map((row) => row.rowKey),
      );
      lifecycleState.pendingEnterKind = "onListFilterEnter";

      if (activeRows.length === 0) {
        completeExitPhase();
        return lifecycleState.rows;
      }

      if (!hasListFilterExitAnimations) {
        completeExitPhase();
        return lifecycleState.rows;
      }

      lifecycleState.rows = activeRows.map((row) => ({
        ...row,
        isExiting: true,
        exitUntil: nowMs,
        transitionDispatch: createListTransitionDispatch(
          "onListFilterExit",
          row.rowKey,
        ),
      }));
      return lifecycleState.rows;
    }

    const currentActiveKeys = new Set(
      lifecycleState.rows
        .filter((row) => !row.isExiting)
        .map((row) => row.rowKey),
    );
    const nextKeys = new Set(sourceRows.map((row) => row.rowKey));
    const removedKeys = [...currentActiveKeys].filter(
      (rowKey) => !nextKeys.has(rowKey),
    );
    const addedKeys = new Set(
      sourceRows
        .filter((row) => !currentActiveKeys.has(row.rowKey))
        .map((row) => row.rowKey),
    );

    if (removedKeys.length > 0) {
      if (filterKeyChanged && !hasListFilterExitAnimations) {
        lifecycleState.phase = "idle";
        lifecycleState.pendingRows = undefined;
        lifecycleState.pendingEnterKeys = undefined;
        lifecycleState.pendingEnterKind = undefined;
        lifecycleState.rows = sourceRows.map((row) =>
          addedKeys.has(row.rowKey)
            ? withEnterDispatch(row, "onListFilterEnter")
            : row,
        );
        return lifecycleState.rows;
      }

      if (!hasRowExitAnimations) {
        lifecycleState.phase = "idle";
        lifecycleState.pendingRows = undefined;
        lifecycleState.pendingEnterKeys = undefined;
        lifecycleState.pendingEnterKind = undefined;
        lifecycleState.rows = sourceRows.map((row) =>
          addedKeys.has(row.rowKey) ? withEnterDispatch(row) : row,
        );
        return lifecycleState.rows;
      }

      const removedKeySet = new Set(removedKeys);
      lifecycleState.phase = "exiting";
      lifecycleState.pendingRows = sourceRows;
      lifecycleState.pendingEnterKeys = addedKeys;
      lifecycleState.pendingEnterKind = "onListEnter";
      lifecycleState.rows = lifecycleState.rows
        .filter((row) => !row.isExiting)
        .map((row) =>
          removedKeySet.has(row.rowKey)
            ? {
                ...row,
                isExiting: true,
                exitUntil: nowMs,
                transitionDispatch: createListTransitionDispatch(
                  "onListExit",
                  row.rowKey,
                ),
              }
            : row,
        );
      return lifecycleState.rows;
    }

    lifecycleState.phase = "idle";
    lifecycleState.pendingRows = undefined;
    lifecycleState.pendingEnterKeys = undefined;
    lifecycleState.pendingEnterKind = undefined;
    lifecycleState.rows = sourceRows.map((row) =>
      addedKeys.has(row.rowKey) ? withEnterDispatch(row) : row,
    );
    return lifecycleState.rows;
  };

  const clearConsumedTransitionDispatches = () => {
    lifecycleState.rows = lifecycleState.rows.map((row) =>
      row.transitionDispatch ? { ...row, transitionDispatch: undefined } : row,
    );
  };

  const scheduleListTransitionWake = (ctx: RuntimeContext, wakeAt: number) => {
    ctx.__runtimeContinuations?.push({
      key: `${node.id}:list-transition:${Math.round(wakeAt)}`,
      flowKey: `${node.id}:list-transition`,
      wakeAt,
      nodeId: node.id,
      ctx,
    });
  };

  const selectTemplateForRow = (
    rowCtx: RuntimeContext,
  ): CompiledRowTemplate => {
    const effectiveMode = getEffectiveListMode(props, rowCtx);

    if (effectiveMode === "edit") {
      const editName = String(props.editRowVariantName ?? "").trim();
      if (editName) {
        return variantTemplates.get(editName) ?? defaultTemplate;
      }
      return defaultTemplate;
    }

    for (const rule of rowVariantRules) {
      if (coerceToBoolean(rule.condition.evaluate(rowCtx))) {
        return variantTemplates.get(rule.variantName) ?? defaultTemplate;
      }
    }

    return defaultTemplate;
  };

  const createTimedFlowHandlers = (
    handlers: ListFilterTransitionHandler[],
    ctx: RuntimeContext,
    listGeometry: RuntimeCurrentNodeGeometry,
  ): TimedFlowHandler[] =>
    handlers.map(({ ownerNodeId, handler }) => {
      const ownerNode =
        ownerNodeId === node.id
          ? undefined
          : compileContext.layoutNodeById?.get(ownerNodeId);
      const ownerGeometry = ownerNode
        ? getLayoutNodeGeometry(ownerNode)
        : listGeometry;

      return {
        handler,
        ctx: withCurrentNodeGeometry(ctx, ownerGeometry),
      };
    });

  return {
    id: node.id,
    dependencies: uniqueDependencies([
      ...defaultTemplate.compiled.dependencies,
      ...compiledVariants.flatMap((variant) => variant.compiled.dependencies),
      ...(listSourceKey ? [listSourceKey] : []),
      ...(behavior
        ? collectBehaviorDependencies(behavior, compileContext)
        : []),
      ...rowVariantRules.flatMap((rule) => rule.dependencies),
      ...(computedListSource?.dependencies ?? []),
      ...listFilterRules.flatMap((rule) => rule.dependencies),
      ...listSortRules.flatMap((rule) => rule.dependencies),
      ...listFilterTransitionDependencies,
    ]),

    evaluate(ctx: RuntimeContext): RenderLayoutListNode | null {
      const listDependencies = uniqueDependencies([
        ...defaultTemplate.compiled.dependencies,
        ...compiledVariants.flatMap((variant) => variant.compiled.dependencies),
        ...(listSourceKey ? [listSourceKey] : []),
        ...(behavior
          ? collectBehaviorDependencies(behavior, compileContext)
          : []),
        ...listFilterRules.flatMap((rule) => rule.dependencies),
        ...listSortRules.flatMap((rule) => rule.dependencies),
        ...(computedListSource?.dependencies ?? []),
        ...rowVariantRules.flatMap((rule) => rule.dependencies),
        ...listFilterTransitionDependencies,
      ]);

      return evaluateWithRenderCache(ctx, node.id, listDependencies, () => {
        const listCtx: RuntimeContext = listSourceKey
          ? { ...ctx, __layoutListBindingSource: listSourceKey }
          : ctx;
        const effectiveMode = getEffectiveListMode(props, listCtx);
        const computedRows = computedListSource?.evaluate(listCtx);
        const rawRows = Array.isArray(computedRows)
          ? computedRows
          : getLayoutListRowsFromBinding(node.dataBinding, listCtx);

        const transformedRows =
          effectiveMode === "edit"
            ? rawRows.map((item, originalIndex) => ({ item, originalIndex }))
            : transformLayoutListRows(
                rawRows,
                listCtx,
                listFilterRules,
                listSortRules,
                listSourceKey,
              );

        const visibleRows =
          effectiveMode === "edit"
            ? transformedRows.slice(0, 1)
            : transformedRows.slice(0, Math.max(0, maxItems));
        const nowMs = ctx.__nowMs ?? 0;
        const sourceRows: SourceRowEntry[] = visibleRows.map((rowEntry) => ({
          item: rowEntry.item,
          originalIndex: rowEntry.originalIndex,
          rowKey:
            effectiveMode === "edit"
              ? "edit"
              : deriveLayoutListRowKey(
                  rowEntry.item,
                  props.rowKeyPath,
                  rowEntry.originalIndex,
                ),
        }));
        const currentListFilterKey =
          filterKeyDependencies.length > 0
            ? stableSerialize(
                filterKeyDependencies
                  .slice()
                  .sort()
                  .map((dependency) => [
                    dependency,
                    resolvePath(listCtx, dependency),
                  ]),
              )
            : undefined;

        const evaluateDisplayRows = (displayRows: DisplayRowEntry[]) => {
          const evaluatedRows: Array<{
            render: RenderContainerNode;
            outerWidth: number;
            outerHeight: number;
            template: CompiledRowTemplate;
          }> = [];

          for (
            let displayIndex = 0;
            displayIndex < displayRows.length;
            displayIndex += 1
          ) {
            const rowEntry = displayRows[displayIndex];
            if (!rowEntry) {
              continue;
            }

            const {
              item: rowItem,
              originalIndex,
              rowKey,
              transitionDispatch,
            } = rowEntry;
            const rowInstanceKeyPrefix = `${node.id}:row:${rowKey}:`;
            const rowCtx: RuntimeContext = {
              ...listCtx,
              ...buildLayoutListScopedRowData(listCtx, {
                rowIndex: originalIndex,
                rowItem,
                listSourceKey,
                rowsListData: rawRows,
              }),
              __nodeInstanceKeyPrefix: rowInstanceKeyPrefix,
              __listTransitionDispatches: transitionDispatch
                ? [transitionDispatch]
                : undefined,
            };

            const selectedTemplate = selectTemplateForRow(rowCtx);

            const rowSignature = [
              `template:${selectedTemplate.name}`,
              buildDependencySignature(rowCtx, [
                ...selectedTemplate.compiled.dependencies,
                ...rowVariantRules.flatMap((rule) => rule.dependencies),
              ]),
            ].join("|");

            const rowCacheKey = `${node.id}:row:${rowKey}:${selectedTemplate.name}`;
            const cachedRow = rowRenderCache.get(rowCacheKey);
            const hasPendingRowContinuations = [
              ...(listCtx.__runtimeContinuationStateByNodeKey?.keys() ?? []),
            ].some((stateKey) => stateKey.startsWith(rowInstanceKeyPrefix));
            const hasResumeRowContinuations = (
              listCtx.__resumeContinuations ?? []
            ).some(
              (continuation) =>
                continuation.key.startsWith(rowInstanceKeyPrefix) ||
                continuation.flowKey.startsWith(rowInstanceKeyPrefix),
            );
            const canUseCachedRow =
              !transitionDispatch &&
              !rowEntry.isExiting &&
              !hasPendingRowContinuations &&
              !hasResumeRowContinuations &&
              !(listCtx.__customDisciplineClickDispatches ?? []).some(
                (dispatch) =>
                  dispatch.nodeKey == null ||
                  dispatch.nodeKey.startsWith(rowInstanceKeyPrefix),
              );

            if (canUseCachedRow && cachedRow?.signature === rowSignature) {
              evaluatedRows.push({
                render: cachedRow.render,
                outerWidth: cachedRow.outerWidth,
                outerHeight: cachedRow.outerHeight,
                template: cachedRow.template,
              });
              continue;
            }

            const effectStartIndex = listCtx.__runtimeEffects?.length ?? 0;
            const continuationStartIndex =
              listCtx.__runtimeContinuations?.length ?? 0;
            const renderedRow = selectedTemplate.compiled.evaluate(rowCtx);
            const rowEffects =
              listCtx.__runtimeEffects?.slice(effectStartIndex) ?? [];
            const rowContinuations =
              listCtx.__runtimeContinuations?.slice(continuationStartIndex) ??
              [];

            if (rowEntry.isExiting) {
              const effectUntil = rowEffects.reduce(
                (maxUntil, effect) =>
                  Math.max(maxUntil, nowMs + getAnimationEffectWaitMs(effect)),
                nowMs,
              );
              const continuationUntil = rowContinuations.reduce(
                (maxUntil, continuation) =>
                  Math.max(maxUntil, continuation.wakeAt),
                nowMs,
              );
              rowEntry.exitUntil = Math.max(
                rowEntry.exitUntil ?? nowMs,
                effectUntil,
                continuationUntil,
              );
            }

            if (!renderedRow || renderedRow.kind !== "container") {
              continue;
            }

            const rowRenderKey = `${node.id}:row:${rowKey}`;
            const renderedRowKey = renderedRow.key ?? renderedRow.id;
            for (const effect of rowEffects) {
              remapAnimationEffectTargetKey(
                effect,
                renderedRowKey,
                rowRenderKey,
              );
            }

            const entry: RowCacheEntry = {
              signature: rowSignature,
              render: {
                ...renderedRow,
                key: rowRenderKey,
              },
              outerWidth: renderedRow.width,
              outerHeight: renderedRow.height,
              template: selectedTemplate,
            };

            rowRenderCache.set(rowCacheKey, entry);

            evaluatedRows.push({
              render: entry.render,
              outerWidth: entry.outerWidth,
              outerHeight: entry.outerHeight,
              template: entry.template,
            });
          }

          return evaluatedRows;
        };

        let displayRows = syncLifecycleRows(
          sourceRows,
          nowMs,
          effectiveMode,
          currentListFilterKey,
          hasListFilterTransitionHandlers,
        );
        let evaluatedRows = evaluateDisplayRows(displayRows);
        let maxExitUntil = lifecycleState.rows.reduce(
          (max, row) =>
            Math.max(max, row.isExiting ? (row.exitUntil ?? nowMs) : nowMs),
          nowMs,
        );

        if (lifecycleState.phase === "exiting" && maxExitUntil <= nowMs) {
          completeExitPhase();
          displayRows = lifecycleState.rows;
          evaluatedRows = evaluateDisplayRows(displayRows);
          maxExitUntil = nowMs;
        }

        clearConsumedTransitionDispatches();

        if (lifecycleState.phase === "exiting" && maxExitUntil > nowMs) {
          scheduleListTransitionWake(listCtx, maxExitUntil);
        }

        const positionedRows = resolveLayoutListRowOffsets(
          evaluatedRows,
          direction,
        );

        const children: RenderContainerNode[] = positionedRows.map((row) => ({
          ...(offsetRenderNodePosition(
            row.render,
            row.template.template.x + row.offsetX - row.render.x,
            row.template.template.y + row.offsetY - row.render.y,
          ) as RenderContainerNode),
          hasTopLeftTransition:
            row.render.hasTopLeftTransition ??
            transitionsTouchTopLeft(row.template.template.transitions),
        }));

        const requiresDirectChildTopLeftFlip = children.some(
          (child) => child.hasTopLeftTransition === true,
        );

        const stackedWidth = positionedRows.reduce(
          (max, row) => Math.max(max, row.offsetX + row.outerWidth),
          0,
        );
        const stackedHeight = positionedRows.reduce(
          (max, row) => Math.max(max, row.offsetY + row.outerHeight),
          0,
        );

        let renderNode: RenderLayoutListNode = {
          id: node.id,
          key: ctx.__nodeInstanceKeyPrefix
            ? `${ctx.__nodeInstanceKeyPrefix}${node.id}`
            : node.id,
          kind: "layoutList",
          x: node.x,
          y: node.y,
          mode: effectiveMode,
          width: stackedWidth,
          height: stackedHeight,
          rotation: node.rotation ?? 0,
          zIndex: node.zIndex,
          direction,
          style: { ...baseStyle },
          children,
          requiresDirectChildTopLeftFlip,
        };

        const listGeometry = {
          x: node.x,
          y: node.y,
          width: renderNode.width,
          height: renderNode.height,
          rotation: node.rotation ?? 0,
        };
        const behaviorCtx = withCurrentNodeGeometry(listCtx, listGeometry);
        const listFilterExitSteps = collectTimedFlowSteps(
          createTimedFlowHandlers(
            externalListFilterExitHandlers,
            listCtx,
            listGeometry,
          ),
          nowMs,
        );
        const listFilterEnterSteps = collectTimedFlowSteps(
          createTimedFlowHandlers(
            externalListFilterEnterHandlers,
            listCtx,
            listGeometry,
          ),
          nowMs,
        );
        const listFilterExitEffects = listFilterExitSteps.flatMap(
          (step) => step.effects,
        );
        const listFilterEnterEffects = listFilterEnterSteps.flatMap(
          (step) => step.effects,
        );
        const hasListFilterTransitionPresence =
          listFilterExitSteps.length > 0 || listFilterEnterSteps.length > 0;
        const listFilterExitDurationMs = listFilterExitSteps.reduce(
          (maxDuration, step) =>
            Math.max(maxDuration, getTimedStepDurationMs(step)),
          0,
        );
        const listFilterEnterDurationMs = listFilterEnterSteps.reduce(
          (maxDuration, step) =>
            Math.max(maxDuration, getTimedStepDurationMs(step)),
          0,
        );
        const listFilterKey =
          hasListFilterTransitionPresence && currentListFilterKey != null
            ? currentListFilterKey
            : undefined;
        renderNode = {
          ...renderNode,
          hasListFilterTransitionPresence,
          listFilterExitEffects,
          listFilterEnterEffects,
          listFilterExitSteps,
          listFilterEnterSteps,
          listFilterExitDurationMs,
          listFilterEnterDurationMs,
          listFilterKey,
        };

        if (behavior) {
          const { patches, effects, continuations, runtimeDomTarget } =
            runCompiledNodeBehavior(behavior, behaviorCtx, node.id);

          for (const patch of patches) {
            renderNode = applyPatchToLayoutListNode(renderNode, patch);
          }

          listCtx.__runtimeEffects?.push(...effects);
          listCtx.__runtimeContinuations?.push(...continuations);
          renderNode = applyRuntimeDomTargetFlags(
            renderNode,
            runtimeDomTarget,
            behavior,
          );
        }

        if (renderNode.style.visibility === false) {
          return null;
        }

        return renderNode;
      });
    },
  };
}
